import React, { useState, useEffect } from 'react';
import enhancedAPI, { Project } from '../services/enhancedApi';
import ProjectChatView from './ProjectChatView';
import { FolderIcon, PlusIcon, EditIcon, TrashIcon, FileTextIcon, ClockIcon } from './Icons';
import './ProjectsManagement.css';

// Predefined prompt templates
const PROMPT_TEMPLATES = [
  {
    id: 'stablecoin',
    title: 'Stablecoin Adoption Analysis',
    description: 'Analyze the current state and future potential of stablecoin adoption in banking',
    prompt: 'Analyze the current status of stablecoin adoption in banking, including market trends, regulatory landscape, technology readiness, and provide strategic recommendations.'
  },
  {
    id: 'digital-banking',
    title: 'Digital Banking Strategy',
    description: 'Evaluate digital banking strategies and competitive positioning',
    prompt: 'Compare digital banking strategies with regional competitors, analyze market trends, customer expectations, and recommend strategic improvements.'
  },
  {
    id: 'blockchain',
    title: 'Blockchain Implementation',
    description: 'Assess blockchain technology for banking operations',
    prompt: 'Assess the risks and opportunities of implementing blockchain-based payment systems, including technical requirements, regulatory considerations, and ROI analysis.'
  },
  {
    id: 'customer-experience',
    title: 'Customer Experience Analysis',
    description: 'Analyze customer sentiment and experience metrics',
    prompt: 'Perform a comprehensive customer sentiment analysis for mobile banking applications, identify pain points, and suggest improvements based on user feedback and industry best practices.'
  },
  {
    id: 'risk-assessment',
    title: 'Risk Assessment',
    description: 'Comprehensive risk analysis for new initiatives',
    prompt: 'Conduct a comprehensive risk assessment for [specific initiative], including operational, financial, regulatory, and reputational risks with mitigation strategies.'
  },
  {
    id: 'custom',
    title: 'Custom Analysis',
    description: 'Create your own custom analysis query',
    prompt: ''
  }
];

const ProjectsManagement: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    initial_query: ''
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const projectList = await enhancedAPI.listProjects();
      setProjects(projectList);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = PROMPT_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setNewProject({
        ...newProject,
        initial_query: template.prompt
      });
    }
  };

  const createProject = async () => {
    if (!newProject.name.trim()) {
      alert('Please enter a project name');
      return;
    }

    try {
      const project = await enhancedAPI.createProject(
        newProject.name,
        newProject.description,
        newProject.initial_query
      );
      
      // Reset form
      setNewProject({ name: '', description: '', initial_query: '' });
      setSelectedTemplate('');
      setIsCreating(false);
      
      // Reload projects
      await loadProjects();
      
      // Navigate to the new project
      setSelectedProject(project);
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project. Please try again.');
    }
  };

  const deleteProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await enhancedAPI.deleteProject(projectId);
        setProjects(projects.filter(p => p.project_id !== projectId));
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      timeZone: 'Asia/Dubai',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // If a project is selected, show the chat view
  if (selectedProject) {
    return (
      <ProjectChatView 
        project={selectedProject} 
        onBack={() => setSelectedProject(null)} 
      />
    );
  }

  return (
    <div className="projects-management">
      <div className="projects-header">
        <h2>Projects</h2>
        <button
          className="btn btn-primary create-btn"
          onClick={() => setIsCreating(true)}
        >
          <PlusIcon /> Create New Project
        </button>
      </div>

      {isCreating && (
        <div className="create-project-modal">
          <div className="modal-backdrop" onClick={() => setIsCreating(false)} />
          <div className="modal-content">
            <h3>Create New Project</h3>
            
            <div className="form-group">
              <label>Project Name *</label>
              <input
                type="text"
                placeholder="Enter project name"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                className="form-input"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                placeholder="Brief description of the project"
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                className="form-textarea"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Select Analysis Template</label>
              <div className="template-grid">
                {PROMPT_TEMPLATES.map(template => (
                  <div
                    key={template.id}
                    className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                    onClick={() => handleTemplateSelect(template.id)}
                  >
                    <h4>{template.title}</h4>
                    <p>{template.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {selectedTemplate === 'custom' && (
              <div className="form-group">
                <label>Custom Query</label>
                <textarea
                  placeholder="Enter your custom analysis query"
                  value={newProject.initial_query}
                  onChange={(e) => setNewProject({ ...newProject, initial_query: e.target.value })}
                  className="form-textarea"
                  rows={4}
                />
              </div>
            )}

            <div className="modal-actions">
              <button onClick={createProject} className="btn btn-primary">
                Create Project
              </button>
              <button onClick={() => setIsCreating(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading">Loading projects...</div>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.length === 0 ? (
            <div className="empty-state">
              <FolderIcon />
              <h3>No projects yet</h3>
              <p>Create your first project to get started</p>
            </div>
          ) : (
            projects.map((project) => (
              <div
                key={project.project_id}
                className="project-card"
                onClick={async () => {
                  // Load full project data before selecting
                  try {
                    const fullProject = await enhancedAPI.getProject(project.project_id);
                    console.log('Loaded full project:', fullProject.project_id, 'with', fullProject.conversation_history?.length || 0, 'conversation entries');
                    setSelectedProject(fullProject);
                  } catch (error) {
                    console.error('Failed to load project details:', error);
                    // Fallback to basic project data
                    setSelectedProject(project);
                  }
                }}
              >
                <div className="project-card-header">
                  <FolderIcon className="project-icon" />
                  <div className="project-actions">
                    <button
                      className="icon-btn"
                      onClick={(e) => deleteProject(project.project_id, e)}
                      title="Delete project"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                
                <div className="project-card-body">
                  <h3>{project.name}</h3>
                  {project.description && (
                    <p className="project-description">{project.description}</p>
                  )}
                  
                  <div className="project-stats">
                    <div className="stat-item">
                      <FileTextIcon />
                      <span>{(project as any).conversation_count || 0} queries</span>
                    </div>
                    <div className="stat-item">
                      <ClockIcon />
                      <span>{formatDate(project.updated_at)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="project-card-footer">
                  <span className={`status-badge status-${project.status}`}>
                    {project.status}
                  </span>
                  <span className="file-count">
                    {project.files?.length || 0} files
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectsManagement;